import React, { useState, useEffect, useContext } from 'react';
import './UpdatedFacilityDetail.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import UpdateRow from './UpdateRow';
import WebLinkList from './WebLinkList';
import convenienceApi from '../../common/api/convenience';
import webLinksAreDifferent from '../../common/webLinksAreDifferent';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import logComponent from '../../common/logComponent';

export default function UpdatedFacilityDetail({ facility, linkToCorporation }) {
  const environmentContext = useContext(EnvironmentContext);
  const [prevFacilityWeblinks, setPrevFacilityWeblinks] = useState(null);
  const [prevFacilityWeblinksError, setPrevFacilityWeblinksError] = useState(false);
  const [prevFacilityWeblinksLoading, setPrevFacilityWeblinksLoading] = useState(false);
  const { updatedFields, relatedMimiFacility: prevFacility } = facility.uiData;
  const { hasToken } = environmentContext;

  if (logComponent('UpdatedFacilityDetail')) { console.debug('UpdatedFacilityDetail rendered'); }

  useEffect(() => {
    if (logComponent('UpdatedFacilityDetail')) { console.debug('UpdatedFacilityDetail onMount'); }
    if (!hasToken) return;

  }, [])

  useEffect(() => {
    if (logComponent('UpdatedFacilityDetail')) { console.debug('UpdatedFacilityDetail useEffect [environmentContext.hasToken]'); }
    if (!hasToken) return;

    loadPrevFacilityWeblinks();
  }, [environmentContext.hasToken])

  const loadPrevFacilityWeblinks = () => {
    if (logComponent('UpdatedFacilityDetail')) { console.debug('UpdatedFacilityDetail loadPrevFacilityWeblinks'); }
    if (!hasToken) return;

    const { relatedMimiFacility } = facility.uiData;
    setPrevFacilityWeblinks(null);
    setPrevFacilityWeblinksError(false);
    setPrevFacilityWeblinksLoading(true);

    convenienceApi.getWeblinksForOrganization(relatedMimiFacility)
      .then(
        result => {
          setPrevFacilityWeblinks(result);
          setPrevFacilityWeblinksError(false);
          setPrevFacilityWeblinksLoading(false);
        },
        error => {
          setPrevFacilityWeblinks(null);
          setPrevFacilityWeblinksError(true);
          setPrevFacilityWeblinksLoading(false);
        }
      );
  }

  const handleCorporationClick = (e) => {
    if (logComponent('UpdatedFacilityDetail')) { console.debug('UpdatedFacilityDetail handleCorporationClick'); }
    const { corporationId } = e.currentTarget.dataset;

    // get facility based on id
    // NOTE: we want == in this case b/c the facilityId coming from the
    // dataset is a string, but the value in the linkedFacility is a number
    const targetCorporation = facility.uiData.linkedCorporations.find(corp => 
      corp.salesforceAccountId &&
      corp.salesforceAccountId == corporationId); // eslint-disable-line

    linkToCorporation(targetCorporation);    
  }

  return (
    <div className="Updated-Facility-Detail">
      <h4 className="Updated-Facility-Detail__heading">{facility.name}</h4>
      <div className="Updated-Facility-Detail__row">
        <p className="Updated-Facility-Detail__instructions">Please accept or reject this updated facility.</p>
        <p className="Updated-Facility-Detail__legend">
          <FontAwesomeIcon
            className="Updated-Facility-Detail__legend-icon"
            icon="sync-alt"></FontAwesomeIcon>
          indicates updated values
        </p>
      </div>
      <h5 className="Updated-Facility-Detail__row">
        <span className="Updated-Facility-Detail__sub-heading">Current values</span>
        <span className="Updated-Facility-Detail__sub-heading">New values</span>
      </h5>
      <ul className="Updated-Facility-Detail__list">
        <UpdateRow
          fieldName="Facility Name"
          currentValue={prevFacility.name}
          newValue={facility.name}
          isUpdated={updatedFields.includes('name')} />
        <UpdateRow
          fieldName="About Us"
          currentValue={prevFacility.aboutUs}
          newValue={facility.aboutUs}
          isUpdated={updatedFields.includes('aboutUs')} />
        <UpdateRow
          fieldName="Postal Code"
          currentValue={prevFacility.postalCode}
          newValue={facility.postalCode}
          isUpdated={updatedFields.includes('postalCode')} />
        <UpdateRow
          fieldName="Industry Type"
          currentValue={prevFacility.industryType}
          newValue={facility.industryType}
          isUpdated={updatedFields.includes('industryType')} />
        <UpdateRow
          fieldName="Using SARA"
          currentValue={prevFacility.usingSara ? 'Yes' : 'No'}
          newValue={facility.usingSara ? 'Yes' : 'No'}
          isUpdated={updatedFields.includes('usingSara')} />
        <UpdateRow
          fieldName="Using CATIE"
          currentValue={prevFacility.usingCatie ? 'Yes' : 'No'}
          newValue={facility.usingCatie ? 'Yes' : 'No'}
          isUpdated={updatedFields.includes('usingCatie')} />
        <UpdateRow
          fieldName="Using EMessenger"
          currentValue={prevFacility.usingEMessenger ? 'Yes' : 'No'}
          newValue={facility.usingEMessenger ? 'Yes' : 'No'}
          isUpdated={updatedFields.includes('usingEMessenger')} />
        <UpdateRow
          fieldName="Salesforce Site Id"
          currentValue={prevFacility.salesforceSiteId}
          newValue={facility.salesforceSiteId}
          isUpdated={updatedFields.includes('salesforceSiteId')} />
        <UpdateRow
          fieldName="Salesforce Account Id"
          currentValue={prevFacility.salesforceAccountId}
          newValue={facility.salesforceAccountId}
          isUpdated={updatedFields.includes('salesforceAccountId')} />
        <UpdateRow
          fieldName="Web Links"
          currentValue={prevFacilityWeblinks && prevFacilityWeblinks.length > 0 ? <WebLinkList links={prevFacilityWeblinks} /> : null}
          newValue={facility.webLinks.length > 0 ? <WebLinkList links={facility.WebLinks} /> : null}
          isUpdated={webLinksAreDifferent(prevFacilityWeblinks, facility.webLinks)}
          isLoading={prevFacilityWeblinksLoading}
          isError={prevFacilityWeblinksError}
          reloadAction={loadPrevFacilityWeblinks} />
        <li key="facilityDetailList" className="Updated-Facility-Detail__list-row">
          <span className="Updated-Facility-Detail__field-name-sm">Linked Corporations
            {
              updatedFields.includes('linkedFacilities') &&
              <FontAwesomeIcon className="Updated-Facility-Detail__legend-icon" icon="sync-alt" />
            }
          </span>
          <div className="Updated-Facility-Detail__item">
            <span className="Updated-Facility-Detail__field-source">CURRENT</span>
            <span className="Updated-Facility-Detail__field-name">Linked Corporations</span>
            <span className="Updated-Facility-Detail__field-value">

              {
                prevFacility.uiData.linkedCorporations.length === 0 ?
                  <span className="Updated-Facility-Detail__empty-field">Not Provided</span> :
                  <ul className="Updated-Facility-corporation-list">
                    {
                      prevFacility.uiData.linkedCorporations.map(corporation =>
                        <li
                          className="Updated-Facility-Detail__corporation-item"
                          key={corporation.salesforceAccountId}>
                          <span className="Updated-Facility-Detail__corporation-id">{corporation.salesforceAccountId}</span>
                          <span className="Updated-Facility-Detail__corporation-name">
                            {corporation.name}
                            {
                              (!corporation.status || corporation.status !== 'rejected') &&
                              <button
                                className="Updated-Facility-Detail__corporation-link"
                                data-corporation-id={corporation.salesforceAccountId}
                                onClick={handleCorporationClick}>
                                <FontAwesomeIcon icon="external-link-alt" />
                              </button>
                            }
                          </span>
                        </li>
                      )
                    }
                  </ul>
              }

            </span>
          </div>
          <div className="Updated-Facility-Detail__item">
            <span className="Updated-Facility-Detail__field-source">NEW</span>
            <span className="Updated-Facility-Detail__field-name">Linked Corporations
              {
                updatedFields.includes('linkedCorporations') &&
                <FontAwesomeIcon className="Updated-Facility-Detail__legend-icon" icon="sync-alt" />
              }
            </span>
            <span className="Updated-Facility-Detail__field-value">

              {
                facility.uiData.linkedCorporations.length === 0 ?
                  <span className="Updated-Facility-Detail__empty-field">Not Provided</span> :
                  <ul className="Updated-Facility-Detail__corporation-list">
                    {
                      facility.uiData.linkedCorporations.map(corporation =>
                        <li
                          className="Updated-Facility-Detail__corporation-item"
                          key={corporation.salesforceAccountId}>
                          <span className="Updated-Facility-Detail__corporation-id">{corporation.salesforceAccountId}</span>
                          <span className="Updated-Facility-Detail__corporation-name">
                            {corporation.name}
                            {
                              (!corporation.status || corporation.status !== 'rejected') &&
                              <button
                                className="Updated-Facility-Detail__corporation-link"
                                data-corporation-id={corporation.salesforceAccountId}
                                onClick={handleCorporationClick}>
                                <FontAwesomeIcon icon="external-link-alt" />
                              </button>
                            }
                          </span>
                        </li>
                      )
                    }
                  </ul>
              }
            </span>
          </div>
        </li>
      </ul>
    </div>
  );
}